/**
 * «Профиль и бизнес»: how the owner signs in (Telegram; a password only for
 * accounts made before it), their phone, and the shop's name and requisites
 * as they appear on receipts and in the registers.
 */
import type { BusinessDto } from '@pos/api-client';
import { ApiError } from '@pos/api-client';
import {
  Badge,
  Button,
  ErrorState,
  Field,
  Input,
  ListGroup,
  ListRow,
  Skeleton,
  toast,
} from '@pos/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  api,
  linkTelegram,
  restore,
  setMyPhone,
  signIn,
  telegramErrorText,
  useAuth,
} from '../../app/auth';
import { TelegramProfile, TelegramSignInButton } from '../../app/SignIn';
import { Screen } from '../../app/Screen';

function PasswordForm() {
  const me = useAuth((s) => s.me);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const weak = next !== '' && (next.length < 8 || !/[A-Za-zА-Яа-я]/.test(next) || !/\d/.test(next));
  const mismatch = again !== '' && again !== next;
  const change = useMutation({
    mutationFn: async () => {
      await api.post('/auth/password', { currentPassword: current, newPassword: next });
      // Changing the password ends every session, this one included: sign
      // straight back in with the new one so the owner stays where they are.
      const login = me?.user.email ?? me?.user.phone;
      if (login) await signIn(login, next);
    },
    onSuccess: () => {
      setCurrent('');
      setNext('');
      setAgain('');
      toast.success('Пароль изменён', { description: 'На других устройствах нужно войти заново' });
    },
    onError: (e) => {
      if (e instanceof ApiError && e.code === 'INVALID_PASSWORD')
        toast.error('Текущий пароль неверен');
      else
        toast.error('Пароль не изменён', {
          description:
            e instanceof ApiError ? (Object.values(e.fields)[0] ?? e.message) : undefined,
        });
    },
  });
  return (
    <div className="space-y-3 rounded-2xl border border-line bg-surface p-4">
      <p className="text-md font-semibold">Сменить пароль</p>
      <Field label="Текущий пароль">
        {(a) => (
          <Input
            {...a}
            size="lg"
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
          />
        )}
      </Field>
      <Field
        label="Новый пароль"
        hint="Не короче 8 символов, буквы и цифры"
        error={weak ? 'Слишком простой пароль' : undefined}
      >
        {(a) => (
          <Input
            {...a}
            size="lg"
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
          />
        )}
      </Field>
      <Field label="Ещё раз" error={mismatch ? 'Пароли не совпадают' : undefined}>
        {(a) => (
          <Input
            {...a}
            size="lg"
            type="password"
            autoComplete="new-password"
            value={again}
            onChange={(e) => setAgain(e.target.value)}
          />
        )}
      </Field>
      <Button
        size="lg"
        variant="primary"
        className="w-full"
        disabled={!current || !next || weak || again !== next}
        loading={change.isPending}
        onClick={() => change.mutate()}
      >
        Сменить пароль
      </Button>
    </div>
  );
}

/** Telegram is the way in; an account from before it connects it here. */
function TelegramSection() {
  const me = useAuth((s) => s.me);
  if (!me) return null;
  const tg = me.user.telegram;
  return (
    <div className="space-y-3 rounded-2xl border border-line bg-surface p-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-md font-semibold">Вход через Telegram</p>
        {tg ? <Badge tone="success">Подключён</Badge> : <Badge tone="warning">Не подключён</Badge>}
      </div>
      {tg ? (
        <>
          <TelegramProfile name={me.user.name} username={tg.username} photoUrl={tg.photoUrl} />
          <p className="text-sm text-ink-3">
            Входите в Gertsak через этот Telegram
            {tg.linkedAt ? ` — подключён ${new Date(tg.linkedAt).toLocaleDateString('ru-RU')}` : ''}
            .
          </p>
        </>
      ) : (
        <>
          <p className="text-base text-ink-2">
            Подключите Telegram — и входите без пароля. Пароль продолжит работать, пока вы им
            пользуетесь.
          </p>
          <TelegramSignInButton
            label="Подключить Telegram"
            onToken={async (idToken, nonce) => {
              try {
                await linkTelegram(idToken, nonce);
                toast.success('Telegram подключён', {
                  description: 'Теперь можно входить через Telegram',
                });
                return null;
              } catch (e) {
                return telegramErrorText(e);
              }
            }}
          />
        </>
      )}
    </div>
  );
}

function PhoneForm() {
  const me = useAuth((s) => s.me);
  const [phone, setPhone] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => setMyPhone(phone ?? ''),
    onSuccess: () => {
      setPhone(null);
      toast.success('Номер сохранён');
    },
    onError: (e) =>
      toast.error(
        e instanceof ApiError && e.isNetwork ? 'Нет связи с сервером' : 'Номер не сохранён',
        {
          description:
            e instanceof ApiError && !e.isNetwork
              ? (Object.values(e.fields)[0] ?? e.message)
              : undefined,
        },
      ),
  });
  if (!me) return null;
  const current = me.user.phone;
  const shown = phone ?? current ?? '';
  return (
    <div className="space-y-3 rounded-2xl border border-line bg-surface p-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-md font-semibold">Телефон</p>
        {current &&
          (me.user.phoneVerified ? (
            <Badge tone="success">Подтверждён Telegram</Badge>
          ) : (
            <Badge tone="neutral">Не подтверждён</Badge>
          ))}
      </div>
      {me.user.phoneVerified && current ? (
        <p className="text-base">{current}</p>
      ) : (
        <>
          {!current && (
            <p className="text-base text-ink-2">
              Добавьте номер — по нему с вами свяжется поддержка.
            </p>
          )}
          <Field label="Номер телефона" hint="Например, +998 90 123 45 67">
            {(a) => (
              <Input
                {...a}
                size="lg"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                value={shown}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+998"
              />
            )}
          </Field>
          <Button
            size="lg"
            variant="primary"
            className="w-full"
            disabled={phone === null || phone.trim() === '' || phone.trim() === current}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            Сохранить номер
          </Button>
        </>
      )}
    </div>
  );
}

function BusinessForm({ canManage }: { canManage: boolean }) {
  const biz = useAuth((s) => s.businessId);
  const qc = useQueryClient();
  const b = useQuery({
    queryKey: ['business', biz],
    queryFn: () => api.get<BusinessDto>('/business'),
  });
  const [form, setForm] = useState<{ name: string; legalName: string; taxId: string } | null>(null);
  const shown =
    form ??
    (b.data
      ? { name: b.data.name, legalName: b.data.legalName ?? '', taxId: b.data.taxId ?? '' }
      : null);
  const save = useMutation({
    mutationFn: () =>
      api.patch('/business', {
        name: shown?.name.trim(),
        legalName: shown?.legalName.trim() || null,
        taxId: shown?.taxId.trim() || null,
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['business'] });
      // The business name also lives in «me» (the header, the business picker).
      await restore();
      setForm(null);
      toast.success('Сохранено', { description: 'Кассы получат новое название при синхронизации' });
    },
    onError: (e) =>
      toast.error('Не сохранено', {
        description: e instanceof ApiError ? (Object.values(e.fields)[0] ?? e.message) : undefined,
      }),
  });
  if (b.isError)
    return (
      <ErrorState
        description="Не удалось загрузить данные бизнеса."
        onRetry={() => void b.refetch()}
      />
    );
  if (!shown) return <Skeleton className="h-48 rounded-2xl" />;
  const set = (k: 'name' | 'legalName' | 'taxId') => (e: { target: { value: string } }) =>
    setForm({ ...shown, [k]: e.target.value });
  const dirty = form !== null;
  return (
    <div className="space-y-3 rounded-2xl border border-line bg-surface p-4">
      <p className="text-md font-semibold">Бизнес</p>
      <Field label="Название" hint="Печатается на чеке и видно на кассах" required>
        {(a) => (
          <Input {...a} size="lg" value={shown.name} disabled={!canManage} onChange={set('name')} />
        )}
      </Field>
      <Field label="Юридическое название">
        {(a) => (
          <Input
            {...a}
            size="lg"
            value={shown.legalName}
            disabled={!canManage}
            onChange={set('legalName')}
            placeholder="ООО «…»"
          />
        )}
      </Field>
      <Field label="ИНН">
        {(a) => (
          <Input
            {...a}
            size="lg"
            inputMode="numeric"
            value={shown.taxId}
            disabled={!canManage}
            onChange={set('taxId')}
          />
        )}
      </Field>
      {canManage ? (
        <Button
          size="lg"
          variant="primary"
          className="w-full"
          disabled={!dirty || shown.name.trim().length < 2}
          loading={save.isPending}
          onClick={() => save.mutate()}
        >
          Сохранить
        </Button>
      ) : (
        <p className="text-sm text-ink-3">Менять реквизиты может владелец или администратор.</p>
      )}
    </div>
  );
}

export function ProfilePage() {
  const me = useAuth((s) => s.me);
  const businessId = useAuth((s) => s.businessId);
  const membership = me?.memberships.find((m) => m.businessId === businessId);
  return (
    <Screen title="Профиль и бизнес" back="/more">
      <div className="space-y-4">
        {me && (
          <ListGroup>
            <ListRow title={me.user.name} subtitle="Имя" />
            {me.user.email && <ListRow title={me.user.email} subtitle="Email" />}
            {membership && <ListRow title={membership.roleName} subtitle="Роль в бизнесе" />}
          </ListGroup>
        )}
        <TelegramSection />
        <PhoneForm />
        <BusinessForm canManage={membership?.permissions.includes('settings.manage') ?? false} />
        {me?.user.hasPassword !== false && <PasswordForm />}
      </div>
    </Screen>
  );
}
